import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { ctxIsPDF, ctxIsTemplate } from '@ContractBuilder/rules/block/is-ctx';
import { useSchemaStore } from '@ContractBuilder/store';
import type { EntityData } from '@ContractBuilder/types';
import { getDocumentContext, getEndorsementMentionValues, getMentionValues } from '@ContractBuilder/utils';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import type { ResourceBlock } from '@root/@types/base';
import type { BlockMentionValues } from '@root/@types/types';
import { isNonNullish } from '@root/helpers';
import { getClausesAsMentions, getGroupedClauses } from '@root/helpers/clauses';

interface UseMentionValuesProps {
  block: ResourceBlock;
  endorsementParent?: EntityData;
  schemaTreeForPdf?: any;
  submission?: EntityData;
}

export const useMentionValues = ({ block, endorsementParent, schemaTreeForPdf, submission }: UseMentionValuesProps) => {
  const context = getDocumentContext();

  const isPDFRender = ctxIsPDF(context);
  const isTemplate = ctxIsTemplate(context);

  const isEditing = useBlockEditFormStore(({ formValues }) => formValues?.id === block.id);
  const { schemaTreeList, schema } = useSchemaStore(({ schemaTreeList, schema }) => ({
    schemaTreeList,
    schema,
  }));

  const mentionValues = useDeepCompareMemo(() => {
    if (isTemplate) {
      return {};
    }
    const usedClauses = getGroupedClauses(submission);

    const blockLinkedDatapointIds = (block.linkedDatapoints ?? []).map(({ id }) => id).filter(isNonNullish);
    const variations = block.variations ?? [];
    const variationsLinkedDatapointsCombined = variations
      .map(({ linkedDatapoints }) => linkedDatapoints)
      .flat()
      .filter(isNonNullish);

    const combinedDatapointIdsSet = new Set([
      ...blockLinkedDatapointIds,
      ...variationsLinkedDatapointsCombined.map(({ id }) => id),
    ]);

    const mentions = getMentionValues(
      submission?.data_items,
      [...combinedDatapointIdsSet],
      isPDFRender ? schemaTreeForPdf : schemaTreeList,
      schema?.definitions,
    );

    const endorsementMentionValues = getEndorsementMentionValues(
      submission,
      blockLinkedDatapointIds,
      endorsementParent,
    );
    const clausesList = getClausesAsMentions(usedClauses);

    return { ...mentions, ...endorsementMentionValues, ...clausesList } satisfies BlockMentionValues;
    // eslint-disable-next-line -- Skipping schema tree list changes
  }, [block.linkedDatapoints, submission, isEditing, isPDFRender, endorsementParent, isTemplate]);

  return mentionValues;
};
